/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import { useContext } from 'react';

import { assert } from '@gitbook/util-ts';

import { LoadingIndicator } from '../Loading';
import { SearchInput } from '../TextInput';
import { AutoCompleteStateContext } from './AutoComplete';

export type MenuSearchProps = Pick<InternalMenuSearchProps, 'label' | 'isPending' | 'variant'>;

/** Search linked to an AutoComplete menu; unlike SearchInput, it shares the menu's keyboard focus. */
export function MenuSearch(props: MenuSearchProps) {
    return <InternalMenuSearch {...props} />;
}

export function HiddenMenuSearch() {
    const ctx = useContext(AutoCompleteStateContext);
    assert(ctx, 'Must be used in a Menu');
    const isHidden = Boolean(ctx && !ctx?.isTouched);
    return <InternalMenuSearch isHidden={isHidden} />;
}

type InternalMenuSearchProps = {
    /**
     * Label used as aria-label and placeholder.
     */
    label?: string | undefined;

    /**
     * Show a pending indicator on the end.
     * @default false
     */
    isPending?: boolean;

    /** Use a bordered SearchInput with a clear button, or the default full-width plain field. */
    variant?: 'plain' | 'input';

    /**
     * Hide it.
     */
    isHidden?: boolean;
};

function InternalMenuSearch(props: InternalMenuSearchProps) {
    const { label = 'Filter…', isHidden, isPending, variant = 'plain' } = props;
    const ctx = useContext(AutoCompleteStateContext);
    assert(ctx, 'Must be used in a Menu');
    const inputProps = {
        ref: ctx.internalSearchInputRef,
        'data-1p-ignore': true,
        'data-lpignore': true,
        'data-ignore-overlay-close': true,
        spellCheck: false,
        autoComplete: 'off',
        autoCorrect: 'off',
        placeholder: label,
        'aria-label': label,
        // Hidden searches catch physical typing without opening a phone's on-screen keyboard.
        inputMode: isHidden ? ('none' as const) : undefined,
        type: 'search',
        value: ctx.value,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => ctx.setValue(event.target.value),
    };
    return (
        <div
            className={clsx(
                isHidden
                    ? 'absolute -left-[9999px] -m-px size-px overflow-hidden border-0 p-0 whitespace-nowrap [clip:rect(0,0,0,0)]'
                    : clsx('relative flex shrink-0', variant === 'input' && 'p-2')
            )}
            data-menu-search=""
            data-hidden={isHidden ? 'true' : 'false'}
        >
            <form
                className="contents"
                autoComplete="off"
                onSubmit={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                {variant === 'input' ? (
                    <SearchInput
                        {...inputProps}
                        onClear={() => ctx.setValue('')}
                        trailing={
                            isPending ? <LoadingIndicator className="size-3 shrink-0" /> : null
                        }
                    />
                ) : (
                    <input
                        {...inputProps}
                        className={clsx(
                            'bg-transparent',
                            'w-0 min-w-0 flex-1 border-b px-3.5 py-2 text-ui-base',
                            'appearance-none [&::-webkit-search-cancel-button]:hidden',
                            isPending && 'pr-7'
                        )}
                    />
                )}
                {isPending && variant === 'plain' ? (
                    <LoadingIndicator className="pointer-events-none absolute top-2 right-2 mt-px size-3 transition" />
                ) : null}
            </form>
        </div>
    );
}
